/* eslint-disable react/prop-types */
import { MagnifyingGlass } from '@phosphor-icons/react';
import { Node } from '@tiptap/pm/model';
import { Plugin } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { Extension, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { Command } from 'cmdk';

import { useFormController } from '~/components/ui/form';
import {
  RichTextEditorContent,
  RichTextEditorProvider,
} from '~/components/ui/rich-text-editor';

const findColors = (doc: Node): DecorationSet => {
  const operatorKeyword = /\b(AND|OR|NOT)\b/g;
  const decorations: Decoration[] = [];

  doc.descendants((node, position) => {
    if (!node.text) {
      return;
    }

    Array.from(node.text.matchAll(operatorKeyword)).forEach((match) => {
      const color = match[0];
      const index = match.index || 0;
      const from = position + index;
      const to = from + color.length;
      const decoration = Decoration.inline(from, to, {
        class: 'text-blue-400 font-medium rounded',
      });

      decorations.push(decoration);
    });
  });

  return DecorationSet.create(doc, decorations);
};

const ColorHighlighter = Extension.create({
  name: 'colorHighlighter',

  addProseMirrorPlugins() {
    return [
      new Plugin({
        state: {
          init(_, { doc }) {
            return findColors(doc);
          },
          apply(transaction, oldState) {
            return transaction.docChanged
              ? findColors(transaction.doc)
              : oldState;
          },
        },
        props: {
          decorations(state) {
            return this.getState(state);
          },
        },
      }),
    ];
  },
});

const PreventEnter = Extension.create({
  addKeyboardShortcuts(this) {
    return {
      Enter: () => true,
    };
  },
});

export function KeywordAwareInput() {
  const controller = useFormController();
  const editor = useEditor({
    extensions: [StarterKit, PreventEnter, ColorHighlighter],
    autofocus: 'start',
    editorProps: {
      attributes: {
        spellcheck: 'false',
        class:
          'prose prose-base focus:outline-none h-7 p-0 [&>p]:my-0 [&>p]:h-full [&>p]:leading-7',
      },
    },
    onUpdate(props) {
      if (controller) {
        controller.field.onChange(props.editor.getText());
      }
    },
    onBlur: () => {
      if (controller) {
        controller.field.onBlur();
      }
    },
  });

  return (
    <RichTextEditorProvider value={{ editor, editable: true }}>
      <div className='relative flex items-center gap-2'>
        <Command.Input
          className='sr-only'
          value={controller?.field.value ?? ''}
        />
        <MagnifyingGlass size={16} />
        <RichTextEditorContent />
      </div>
    </RichTextEditorProvider>
  );
}
